import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heading1, Paragraph, Button, Link } from '@shopmuchmore/design-system';
import { Globe, Heart, ShieldCheck, Users, MapPin } from 'lucide-react';
import { env } from '../../../config/env';
import { useLocationStore } from '../../local/store/location.store';

const IMAGES = [
  '/images/backgrounds/intro-banner-1.webp',
  '/images/backgrounds/coastal_city_aerial_v2.jpg'
];

function MerchantsSlide({ isActive }: { isActive: boolean }) {
  const { latitude, longitude } = useLocationStore();

  return (
    <div className={`transition-all duration-700 absolute inset-0 flex items-center ${isActive ? 'opacity-100 translate-x-0 z-20' : 'opacity-0 translate-x-8 pointer-events-none z-10'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-6 sm:py-12 lg:py-16 w-full grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-12 items-center">
        {/* Left Side: Text content */}
        <div className="space-y-3 sm:space-y-5">
          <div className="space-y-2 sm:space-y-3">
            <Heading1 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight tracking-tight">
              Local Merchants, <br />
              <span className="text-primary italic">Global Flavours.</span>
            </Heading1>
            <Paragraph size="lg" className="text-gray-300 max-w-xl leading-relaxed text-xs sm:text-base lg:text-lg font-normal">
              {!latitude || !longitude ? 
                "Set your delivery location to discover authentic global ingredients from independent merchants near you." :
                "Explore trusted independent sellers in your area offering traditional foods, spices, and cultural essentials."
              }
            </Paragraph>
          </div>
          <div className="pt-1 sm:pt-3 flex flex-wrap gap-2.5 sm:gap-4">
            <Button asChild className="text-xs sm:text-sm h-9 sm:h-11 px-4 sm:px-6">
              <Link href="/nearby-merchants" className="no-underline text-white hover:text-white">
                View All Merchants
              </Link>
            </Button>
            {(!latitude || !longitude) && (
              <div className="flex items-center gap-2 px-3 py-1.5 sm:py-2 rounded-xl bg-primary/20 border border-primary/30">
                 <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary shrink-0" />
                 <span className="text-white font-medium text-[11px] sm:text-xs">Select address for local results</span>
              </div>
            )}
          </div>
        </div>

        {/* Right Side: Map Visualization (Now fully responsive on both mobile and desktop) */}
        <div className="relative h-[220px] xs:h-[260px] sm:h-[300px] lg:h-[380px] w-full rounded-2xl overflow-hidden bg-black/25 sm:bg-transparent border border-white/10 sm:border-none backdrop-blur-xs sm:backdrop-blur-none mt-1 sm:mt-0">
          
          {/* SVG Connecting Lines */}
          <svg viewBox="0 0 400 300" preserveAspectRatio="none" className="absolute inset-0 w-full h-full z-0 pointer-events-none text-primary/60" style={{ filter: 'drop-shadow(0px 0px 4px rgba(34,197,94,0.4))' }}>
            <defs>
              <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="currentColor" />
              </marker>
            </defs>
            {/* From Center (200, 150) to Top-Left (100, 45) */}
            <path d="M 200 150 Q 120 90 100 45" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="6 6" markerEnd="url(#arrow)" />
            {/* From Center (200, 150) to Right (280, 135) */}
            <path d="M 200 150 Q 240 135 280 135" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="6 6" markerEnd="url(#arrow)" />
            {/* From Center (200, 150) to Bottom (140, 255) */}
            <path d="M 200 150 Q 160 210 140 255" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="6 6" markerEnd="url(#arrow)" />
            {/* From Center (200, 150) to Left (80, 180) */}
            <path d="M 200 150 Q 120 180 80 180" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="6 6" markerEnd="url(#arrow)" />
          </svg>

          {/* Buyer Location (Center) */}
          <div className="absolute top-[50%] left-[50%] -translate-x-1/2 -translate-y-1/2 z-20">
            <div className="flex flex-col items-center">
              <div className="bg-primary text-white px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full shadow-lg font-bold text-[10px] sm:text-xs mb-1 sm:mb-2 whitespace-nowrap">
                Your Location
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary/30 rounded-full flex items-center justify-center animate-pulse">
                <div className="w-3 h-3 sm:w-4 sm:h-4 bg-primary rounded-full shadow-[0_0_15px_rgba(34,197,94,1)] border-2 border-white" />
              </div>
            </div>
          </div>

          <div className="absolute top-[8%] left-[4%] sm:left-[8%] z-10">
            <div className="bg-white/95 backdrop-blur-xs text-slate-900 pr-2.5 sm:pr-3 pl-1 sm:pl-1.5 py-1 sm:py-1.5 rounded-full shadow-lg font-bold text-[10px] sm:text-xs md:text-sm flex items-center gap-1.5 sm:gap-2 transform hover:scale-105 transition-transform cursor-default border border-slate-100">
              <img src="https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&q=80" alt="Spice Route" className="w-4 h-4 sm:w-6 sm:h-6 rounded-full object-cover shrink-0" />
              <span className="whitespace-nowrap">Spice Route Market</span>
            </div>
          </div>
          <div className="absolute top-[34%] right-[2%] sm:right-auto sm:left-[68%] z-10">
            <div className="bg-white/95 backdrop-blur-xs text-slate-900 pr-2.5 sm:pr-3 pl-1 sm:pl-1.5 py-1 sm:py-1.5 rounded-full shadow-lg font-bold text-[10px] sm:text-xs md:text-sm flex items-center gap-1.5 sm:gap-2 transform hover:scale-105 transition-transform cursor-default border border-slate-100">
              <img src="https://images.unsplash.com/photo-1578916171728-46686eac8d58?auto=format&fit=crop&q=80" alt="Seoul Beats" className="w-4 h-4 sm:w-6 sm:h-6 rounded-full object-cover shrink-0" />
              <span className="whitespace-nowrap">Seoul Beats</span>
            </div>
          </div>
          <div className="absolute bottom-[6%] left-[16%] sm:bottom-auto sm:top-[82%] sm:left-[25%] z-10">
            <div className="bg-white/95 backdrop-blur-xs text-slate-900 pr-2.5 sm:pr-3 pl-1 sm:pl-1.5 py-1 sm:py-1.5 rounded-full shadow-lg font-bold text-[10px] sm:text-xs md:text-sm flex items-center gap-1.5 sm:gap-2 transform hover:scale-105 transition-transform cursor-default border border-slate-100">
              <img src="https://images.unsplash.com/photo-1533900298318-6b8da08a523e?auto=format&fit=crop&q=80" alt="African Grocery" className="w-4 h-4 sm:w-6 sm:h-6 rounded-full object-cover shrink-0" />
              <span className="whitespace-nowrap">Mama's Grocery</span>
            </div>
          </div>
          <div className="absolute top-[60%] left-[2%] sm:top-[55%] sm:left-[0%] z-10">
             <div className="bg-white/95 backdrop-blur-xs text-slate-900 pr-2.5 sm:pr-3 pl-1 sm:pl-1.5 py-1 sm:py-1.5 rounded-full shadow-lg font-bold text-[10px] sm:text-xs md:text-sm flex items-center gap-1.5 sm:gap-2 transform hover:scale-105 transition-transform cursor-default border border-slate-100">
              <img src="https://images.unsplash.com/photo-1604719312566-8912e9227c6a?auto=format&fit=crop&q=80" alt="Eastern Delights" className="w-4 h-4 sm:w-6 sm:h-6 rounded-full object-cover shrink-0" />
              <span className="whitespace-nowrap">Eastern Delights</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MarketingSlide({ isActive }: { isActive: boolean }) {
  const navigate = useNavigate();
  
  const points = [
    {
      text: "The marketplace for authentic global ingredients",
      icon: <Globe className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />,
    },
    {
      text: "Connecting communities with flavour of home",
      icon: <Heart className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-rose-400" />,
    },
    {
      text: "Discover authentic ingredients from independent global food merchants",
      icon: <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-400" />,
    },
    {
      text: "Where independent food merchants meet customers looking for authentic ingredients",
      icon: <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-400" />,
    },
  ];

  return (
    <div className={`transition-all duration-700 absolute inset-0 flex items-center ${isActive ? 'opacity-100 translate-x-0 z-20' : 'opacity-0 -translate-x-8 pointer-events-none z-10'}`}>
      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-6 sm:py-12 lg:py-16 w-full">
        <div className="lg:w-2/3 xl:w-1/2 space-y-3 sm:space-y-5">
          <div className="space-y-2 sm:space-y-3">
            <Heading1 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight tracking-tight">
              Authentic Products, <br />
              <span className="text-primary italic">Culture Captured.</span>
            </Heading1>
            
            <Paragraph size="lg" className="text-gray-300 max-w-2xl leading-relaxed text-xs sm:text-base lg:text-lg font-normal">
              Shop Much More connects diaspora communities across the UK with authentic products from home—traditional foods, spices, and cultural essentials.
            </Paragraph>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3.5 pt-1">
            {points.map((point, index) => (
              <div 
                key={index}
                className="flex items-center gap-2.5 p-2 sm:p-3 rounded-xl bg-black/45 border border-white/10 hover:bg-black/60 transition-all duration-300"
              >
                <div className="p-1 sm:p-1.5 rounded-lg bg-white/10 flex-shrink-0">
                  {point.icon}
                </div>
                <Paragraph size="sm" className="text-gray-100 font-medium leading-snug text-[11px] sm:text-xs lg:text-sm">
                  {point.text}
                </Paragraph>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2.5 sm:gap-4 pt-1 sm:pt-3">
            <Button 
              onClick={() => navigate('/browse')}
              className="text-xs sm:text-sm h-9 sm:h-11 px-4 sm:px-6"
            >
              Discover the Marketplace
            </Button>
            <Button 
              variant="outline"
              className='border border-white/20 bg-white/10 hover:bg-white/20 text-white text-xs sm:text-sm h-9 sm:h-11 px-4 sm:px-6'
              onClick={() => window.open(`${env.merchantPlatformUrl}manual`, '_blank')}
            >
              Join as a Merchant
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function MarketingHero() {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 2); // 2 slides: Marketing & Merchants
    }, 12000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="relative w-full overflow-hidden bg-gray-900 font-sans">
      <div className="relative min-h-[520px] xs:min-h-[500px] sm:min-h-[560px] lg:min-h-[620px] flex items-center w-full">
        {IMAGES.map((img, index) => (
          <img 
            key={img}
            src={img}
            alt="Hero Background"
            fetchPriority={index === 0 ? "high" : "auto"}
            loading={index === 0 ? "eager" : "lazy"}
            className={`absolute inset-0 z-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${
              index === currentSlide % IMAGES.length ? 'opacity-100' : 'opacity-0'
            }`}
          />
        ))}
        
        {/* Dark Overlay for better visibility */}
        <div className="absolute inset-0 z-10 bg-black/65" />

        {/* Slides */}
        <MarketingSlide isActive={currentSlide === 0} />
        <MerchantsSlide isActive={currentSlide === 1} />

        {/* Navigation Arrows (Hidden on mobile to prevent overlapping content, visible on sm+) */}
        <button 
          onClick={() => setCurrentSlide((prev) => (prev === 0 ? 1 : 0))}
          className="hidden sm:flex absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 z-30 w-9 h-9 sm:w-10 sm:h-10 rounded-full cursor-pointer bg-black/40 hover:bg-black/60 text-white items-center justify-center transition-colors backdrop-blur-sm border border-white/20 shadow-md"
          aria-label="Previous slide"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 sm:w-6 sm:h-6"><path d="m15 18-6-6 6-6"/></svg>
        </button>
        <button 
          onClick={() => setCurrentSlide((prev) => (prev + 1) % 2)}
          className="hidden sm:flex absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 z-30 w-9 h-9 sm:w-10 sm:h-10 rounded-full cursor-pointer bg-black/40 hover:bg-black/60 text-white items-center justify-center transition-colors backdrop-blur-sm border border-white/20 shadow-md"
          aria-label="Next slide"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 sm:w-6 sm:h-6"><path d="m9 18 6-6-6-6"/></svg>
        </button>

        {/* Slide Indicator Dots */}
        <div className="absolute bottom-2.5 sm:bottom-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2">
          {[0, 1].map((idx) => (
            <button
              key={idx}
              onClick={() => setCurrentSlide(idx)}
              className={`transition-all duration-300 rounded-full cursor-pointer ${
                currentSlide === idx ? 'w-6 h-2 bg-primary' : 'w-2 h-2 bg-white/40 hover:bg-white/70'
              }`}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

